import { DatePicker, Form } from "antd";
import { useIntl } from "react-intl";
import { useParams } from "react-router";
import { FormInput } from "~components/Form/FormInput";
import { FormItem } from "~components/Form/FormItem";
import { FormSection } from "~components/Form/FormSection";
import { FormSelect } from "~components/Form/FormSelect";
import { CountryCodes } from "~enums/countryCodes";
import { Genders } from "~enums/genders";
import { PersonStatuses } from "~enums/personStatuses";
import { Sports } from "~enums/sports";
import parentStyles from "../../AddPersonModal.module.css";
import { addPersonFieldHelpers } from "../../constants/addPersonFieldHelpers";
import type { AddPersonForm } from "../../types/AddPersonForm";

type Props = {
  disabled?: boolean;
};

export const PersonDetailsSection = ({ disabled }: Props) => {
  const { formatMessage } = useIntl();
  const { personDetailsFields } = addPersonFieldHelpers;

  const { sport } = useParams();
  const isHockey = sport === Sports.Hockey;

  const form = Form.useFormInstance<AddPersonForm>();

  return (
    <>
      <FormSection title={formatMessage({ id: "person.details" })} fields={personDetailsFields} />
      <div className={parentStyles.columnsContainer}>
        <div>
          <FormInput<AddPersonForm>
            name="nameFullLocal"
            label={formatMessage({ id: "name.local.full" })}
            rules={[
              {
                required: true,
                message: formatMessage(
                  { id: "form.help.required.field" },
                  { field: formatMessage({ id: "name.local.full" }) },
                ),
              },
            ]}
            disabled={disabled}
          />
          <FormItem<AddPersonForm>
            name="dob"
            label={formatMessage({ id: "date.of.birth" })}
            className={parentStyles.dateFormItem}
          >
            <DatePicker format="DD/MM/YYYY" className={parentStyles.dateInput} placeholder="" disabled={disabled} />
          </FormItem>
          <FormSelect<AddPersonForm>
            name="status"
            label={formatMessage({ id: "status" })}
            rules={[
              {
                required: true,
                message: formatMessage({ id: "form.help.required.field" }, { field: formatMessage({ id: "status" }) }),
              },
            ]}
            selectProps={{
              options: [
                PersonStatuses.Active,
                PersonStatuses.Inactive,
                PersonStatuses.Pending,
                PersonStatuses.Deceased,
                PersonStatuses.Unregistered,
              ].map((status) => ({
                label: formatMessage({ id: `roleStatus.${status}` }),
                value: status,
              })),
            }}
            disabled={disabled}
          />
        </div>
        <div>
          <FormInput<AddPersonForm>
            name="nameFullLatin"
            label={formatMessage({ id: "name.latin.full" })}
            disabled={disabled}
          />
          <FormSelect<AddPersonForm>
            name="nationality"
            label={formatMessage({ id: "nationality" })}
            rules={[
              {
                required: true,
                message: formatMessage(
                  { id: "form.help.required.field" },
                  { field: formatMessage({ id: "nationality" }) },
                ),
              },
            ]}
            selectProps={{
              showSearch: true,
              options: Object.values(CountryCodes).map((code) => ({
                label: formatMessage({ id: `countryCode.${code}` }),
                value: code,
              })),
              ...(isHockey && {
                onChange: (value) => {
                  const additionalDetails = form.getFieldValue("additionalDetails") || {};
                  form.setFieldsValue({ additionalDetails: { ...additionalDetails, representation: value } });
                },
              }),
            }}
            disabled={disabled}
          />
        </div>
        <div>
          <FormInput<AddPersonForm>
            name="nameAbbreviated"
            label={formatMessage({ id: "abbreviated.name" })}
            disabled={disabled}
          />
          <FormSelect<AddPersonForm>
            name="gender"
            label={formatMessage({ id: "gender" })}
            rules={[
              {
                required: true,
                message: formatMessage({ id: "form.help.required.field" }, { field: formatMessage({ id: "gender" }) }),
              },
            ]}
            selectProps={{
              options: [Genders.Male, Genders.Female, Genders.Unknown].map((gender) => ({
                label: formatMessage({ id: `gender.${gender}` }),
                value: gender,
              })),
            }}
            disabled={disabled}
          />
        </div>
      </div>
    </>
  );
};
